import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { API_URL } from '../../config';

const Settings = () => {
  const { token, user, login } = useContext(AuthContext);
  
  // Profile State
  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    phone: '',
    usdt_address: '',
    wishlist_alerts: true,
    newsletter_updates: true,
    order_notifications: true
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');

  // Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');

  // Fetch Settings on Load
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/user/settings`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          setProfileData({
            fullName: data.fullName || '',
            email: data.email || '',
            phone: data.phone || '',
            usdt_address: data.usdt_address || '',
            wishlist_alerts: Boolean(data.wishlist_alerts),
            newsletter_updates: Boolean(data.newsletter_updates),
            order_notifications: Boolean(data.order_notifications)
          });
        }
      } catch (err) {
        console.error("Failed to load settings", err);
      }
    };
    if (token) {
      fetchSettings();
    }
  }, [token]);

  const handleProfileChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfileData({
      ...profileData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileMessage('');
    try {
      const res = await fetch(`${API_URL}/api/user/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });
      if (res.ok) {
        setProfileMessage({ type: 'success', text: 'Profile updated successfully!' });
        // Update user context so the sidebar name changes
        if (user) {
          login({ ...user, fullName: profileData.fullName }, token);
        }
      } else {
        setProfileMessage({ type: 'error', text: 'Failed to update profile.' });
      }
    } catch (err) {
      setProfileMessage({ type: 'error', text: 'An error occurred.' });
    }
    setProfileLoading(false);
    
    // Clear message after 3 seconds
    setTimeout(() => setProfileMessage(''), 3000);
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage('');
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return setPasswordMessage({ type: 'error', text: 'New passwords do not match.' });
    }
    
    if (passwordData.newPassword.length < 6) {
      return setPasswordMessage({ type: 'error', text: 'Password must be at least 6 characters.' });
    }

    setPasswordLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/user/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setPasswordMessage({ type: 'error', text: data.error || 'Failed to update password.' });
      }
    } catch (err) {
      setPasswordMessage({ type: 'error', text: 'An error occurred.' });
    }
    setPasswordLoading(false);
    
    setTimeout(() => setPasswordMessage(''), 3000);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[24px] font-bold text-white mb-1">Account Settings</h1>
        <p className="text-[14px] text-on-surface-variant">
          Manage your personal details, secure your account, and configure notifications.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Profile Information Form */}
        <div className="bg-surface-container border border-white/5 rounded-3xl p-8 shadow-lg">
          <h2 className="text-[18px] font-bold text-white mb-6">Profile Information</h2>
          
          <form onSubmit={saveProfile} className="space-y-5">
            <div>
              <label className="block text-[11px] font-bold text-on-surface-variant uppercase tracking-widest mb-2">
                Email Address (Permanent)
              </label>
              <input 
                type="email" 
                name="email"
                value={profileData.email}
                disabled
                className="w-full bg-surface-container-highest border border-white/10 rounded-xl px-4 py-3 text-white/50 text-[14px] cursor-not-allowed outline-none"
              />
            </div>
            
            <div>
              <label className="block text-[11px] font-bold text-on-surface-variant uppercase tracking-widest mb-2">
                Full Name
              </label>
              <input 
                type="text" 
                name="fullName"
                value={profileData.fullName}
                onChange={handleProfileChange}
                required
                className="w-full bg-[#131418] border border-white/10 rounded-xl px-4 py-3 text-white text-[14px] focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
              />
            </div>
            
            <div>
              <label className="block text-[11px] font-bold text-on-surface-variant uppercase tracking-widest mb-2">
                Phone Number
              </label>
              <input 
                type="text" 
                name="phone"
                placeholder="e.g. +1 555-0199"
                value={profileData.phone}
                onChange={handleProfileChange}
                className="w-full bg-[#131418] border border-white/10 rounded-xl px-4 py-3 text-white text-[14px] focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
              />
            </div>
            
            <div>
              <label className="block text-[11px] font-bold text-on-surface-variant uppercase tracking-widest mb-2">
                USDT Wallet Address (TRC20)
              </label>
              <input 
                type="text" 
                name="usdt_address"
                placeholder="T..."
                value={profileData.usdt_address}
                onChange={handleProfileChange}
                className="w-full bg-[#131418] border border-white/10 rounded-xl px-4 py-3 text-white text-[14px] focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
              />
            </div>
            
            <div className="pt-2">
              <label className="block text-[11px] font-bold text-on-surface-variant uppercase tracking-widest mb-4">
                Notification Preferences
              </label>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input 
                    type="checkbox" 
                    name="wishlist_alerts"
                    checked={profileData.wishlist_alerts}
                    onChange={handleProfileChange}
                    className="w-4 h-4 rounded border-white/10 bg-[#131418] text-primary focus:ring-primary focus:ring-offset-0 focus:ring-offset-transparent cursor-pointer" 
                  />
                  <span className="text-[13px] text-silver-text group-hover:text-white transition-colors">Wishlist Price Drop Email Alerts</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input 
                    type="checkbox" 
                    name="newsletter_updates"
                    checked={profileData.newsletter_updates}
                    onChange={handleProfileChange}
                    className="w-4 h-4 rounded border-white/10 bg-[#131418] text-primary focus:ring-primary focus:ring-offset-0 focus:ring-offset-transparent cursor-pointer" 
                  />
                  <span className="text-[13px] text-silver-text group-hover:text-white transition-colors">Newsletter updates (New EA alerts)</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input 
                    type="checkbox" 
                    name="order_notifications"
                    checked={profileData.order_notifications}
                    onChange={handleProfileChange}
                    className="w-4 h-4 rounded border-white/10 bg-[#131418] text-primary focus:ring-primary focus:ring-offset-0 focus:ring-offset-transparent cursor-pointer" 
                  />
                  <span className="text-[13px] text-silver-text group-hover:text-white transition-colors">Order status & delivery notifications</span>
                </label>
              </div>
            </div>

            <button 
              type="submit" 
              disabled={profileLoading}
              className="w-full mt-4 bg-primary text-[#131418] font-bold px-6 py-3 rounded-xl text-[14px] hover:shadow-[0_0_15px_rgba(240,185,11,0.4)] transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center gap-2"
            >
              {profileLoading ? <span className="material-symbols-outlined animate-spin text-[18px]">progress_activity</span> : 'Save Profile Changes'}
            </button>
            
            {profileMessage && (
              <p className={`text-[13px] font-bold text-center ${profileMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                {profileMessage.text}
              </p>
            )}
          </form>
        </div>

        {/* Change Password Form */}
        <div className="bg-surface-container border border-white/5 rounded-3xl p-8 shadow-lg h-fit">
          <h2 className="text-[18px] font-bold text-white mb-6">Change Password</h2>
          
          <form onSubmit={savePassword} className="space-y-5">
            <div>
              <label className="block text-[11px] font-bold text-on-surface-variant uppercase tracking-widest mb-2">
                Current Password
              </label>
              <input 
                type="password" 
                name="currentPassword"
                placeholder="••••••••"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                required
                className="w-full bg-[#131418] border border-white/10 rounded-xl px-4 py-3 text-white text-[14px] focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
              />
            </div>
            
            <div>
              <label className="block text-[11px] font-bold text-on-surface-variant uppercase tracking-widest mb-2">
                New Password
              </label>
              <input 
                type="password" 
                name="newPassword"
                placeholder="••••••••"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                required
                className="w-full bg-[#131418] border border-white/10 rounded-xl px-4 py-3 text-white text-[14px] focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
              />
            </div>
            
            <div>
              <label className="block text-[11px] font-bold text-on-surface-variant uppercase tracking-widest mb-2">
                Confirm New Password
              </label>
              <input 
                type="password" 
                name="confirmPassword"
                placeholder="••••••••"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                required
                className="w-full bg-[#131418] border border-white/10 rounded-xl px-4 py-3 text-white text-[14px] focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
              />
            </div>

            <button 
              type="submit" 
              disabled={passwordLoading}
              className="w-full mt-4 bg-white/5 border border-white/10 text-white font-bold px-6 py-3 rounded-xl text-[14px] hover:bg-white/10 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
            >
              {passwordLoading ? <span className="material-symbols-outlined animate-spin text-[18px]">progress_activity</span> : 'Update Password'}
            </button>
            
            {passwordMessage && (
              <p className={`text-[13px] font-bold text-center ${passwordMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                {passwordMessage.text}
              </p>
            )}
          </form>
        </div>

      </div>
    </div>
  );
};

export default Settings;
